/* src/components/InteractiveOnboarding.js */
import React, { useState, useEffect } from 'react';
import './InteractiveOnboarding.css';

const InteractiveOnboarding = ({ onComplete }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const totalSlides = 5;

    const nextSlide = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(prev => prev + 1);
        } else {
            handleComplete();
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    };

    const handleComplete = () => {
        onComplete();
    };

    // Keyboard Navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') nextSlide();
            if (e.key === 'ArrowLeft') prevSlide();
            if (e.key === 'Escape') handleComplete();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSlide]);

    return (
        <div className="onb-overlay">
            <div className="onb-card">
                
                <div className="onb-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                    
                    {/* SLIDE 1: WELCOME / PHILOSOPHY */}
                    <div className="onb-slide">
                        <div className="onb-text-panel">
                            <div className="onb-badge">Wali's Method</div>
                            <h2>The Bar Exam is a Volume Game.</h2>
                            <p>Passive lectures teach you the law, but Wali's Method ensures you retain it.</p>
                            <p>With Wali's <span className="key-cap">In Limine™</span> learning tool, master the black letter law.</p>
                            <p>Secure that knowledge using <span className="key-cap">De Novo™</span> spaced repetition. Eliminate the need for time-consuming planning.</p>
                            <div className="onb-highlight">
                                <strong>Our Philosophy:</strong> We handle <em>what</em> you study and <em>when</em> you study it. You simply need to show up.
                            </div>
                        </div>
                        <div className="onb-visual-panel">
                            <div className="visual-hero-logo">W</div>
                        </div>
                    </div>

                    {/* SLIDE 2: THE ROUTINE (DOCKET) */}
                    <div className="onb-slide">
                        <div className="onb-text-panel">
                            <div className="onb-badge">The Daily Docket</div>
                            <h2>A Daily Routine for Mastery.</h2>
                            <p>Stop wasting energy deciding <em>what</em> to study. Your <strong>Daily Docket</strong> is a curated list of tasks based on your specific exam date and performance history.</p>
                            <p>It prioritizes rules at risk of decay and schedules new learning automatically.</p>
                            <ul className="onb-list">
                                <li>Review: <span className="key-cap">De Novo™</span> - Spaced Repetition</li>
                                <li>Learn: <span className="key-cap">In Limine™</span>- Learn every key term.</li>
                                <li>Apply: Mini-Essays</li>
                            </ul>
                        </div>
                        <div className="onb-visual-panel">
                            <div className="visual-interface">
                                <div className="sim-header">Today's Docket</div>
                                <div className="sim-row active">
                                    <div className="sim-icon sun"></div>
                                    <div className="sim-text">Review: De Novo™</div>
                                </div>
                                <div className="sim-row">
                                    <div className="sim-icon"></div>
                                    <div className="sim-text">Learn: In Limine™</div>
                                </div>
                                <div className="sim-row">
                                    <div className="sim-icon"></div>
                                    <div className="sim-text">Apply: Mini-Essay</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SLIDE 3: IN LIMINE */}
                    <div className="onb-slide">
                        <div className="onb-text-panel">
                            <div className="onb-badge" style={{color: 'var(--onb-warning)'}}>In Limine™</div>
                            <h2>Active Learning.</h2>
                            <p>Rather than just reading and hoping you remember the key terms, you will <strong>actively</strong> learn them.</p>
                            <p>Our recursive learning process builds <strong>muscle memory</strong> for the black letter law, ensuring you own the elements before you ever see a fact pattern.</p>
                            <div className="onb-highlight">
                                You must type the missing key terms to proceed. This defeats the "illusion of competence" from passive reading.
                            </div>
                        </div>
                        <div className="onb-visual-panel">
                            <div className="visual-interface large">
                                <div className="sim-subtitle">Torts / Intentional Torts</div>
                                <h3 className="sim-h3">Battery</h3>
                                <div className="sim-input-area">
                                    A battery is an <span className="sim-cloze">intentio|</span> infliction...
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SLIDE 4: DE NOVO (RETENTION) */}
                    <div className="onb-slide">
                        <div className="onb-text-panel">
                            <div className="onb-badge" style={{color: 'var(--onb-info)'}}>De Novo™</div>
                            <h2>Beat the Forgetting Horizon.</h2>
                            <p>The <span className="key-cap">De Novo&trade;</span> algorithm tracks the 'memory decay' of every rule you learn. It surfaces material just before you forget it, extending your retention from hours to months.</p>
                            <p>Without actively refreshing your knowledge, you may forget 80% of what you read within 48 hours. Read about our research and algorithms on Wali's Blog.</p>
                        </div>
                        <div className="onb-visual-panel">
                            <svg width="100%" height="100%" viewBox="0 0 400 300" className="onb-graph-svg">
                                {/* Axes */}
                                <line x1="50" y1="250" x2="350" y2="250" stroke="#cbd5e1" strokeWidth="2" />
                                <line x1="50" y1="250" x2="50" y2="50" stroke="#cbd5e1" strokeWidth="2" />
                                
                                <text x="30" y="45" className="graph-label">100%</text>
                                <text x="350" y="275" className="graph-label" textAnchor="end">Time</text>

                                {/* Curve 1: Forgetting (No Review) */}
                                <path d="M 50 60 Q 150 230 350 240" fill="none" stroke="#ef4444" strokeWidth="3" strokeDasharray="8,4" />
                                
                                {/* Review Point */}
                                <circle cx="140" cy="180" r="6" fill="#2E5175" />
                                <line x1="140" y1="180" x2="140" y2="60" stroke="#2E5175" strokeWidth="2" strokeDasharray="4,2" opacity="0.5" />
                                
                                {/* Curve 2: After Review */}
                                <path d="M 140 60 Q 250 100 350 120" fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round"/>
                                
                                <text x="260" y="90" className="graph-annotation boost-label" fill="#22c55e">Review Boost!</text>
                                <text x="260" y="230" className="graph-annotation decay-label" fill="#ef4444">Forgetting</text>
                            </svg>
                        </div>
                    </div>

                    {/* SLIDE 5: READY */}
                    <div className="onb-slide final-slide">
                        <div className="onb-text-panel center-align">
                            <div className="onb-badge">Start Studying</div>
                            <h2>Trust the Process.</h2>
                            <p className="big-text">You shouldn't have to plan <em>what</em> to study. Your only job is to show up and do the work.</p>
                            <p className="quote">With our 24-hour guarantee, we will get back to you within a day for any questions or concerns.</p>
                            <p className="quote">"Leave it to Wali's Method."</p>
                            <br/>
                            <button className="onb-btn large" onClick={handleComplete}>Open My Daily Docket</button>
                        </div>
                    </div>

                </div>

                {/* FOOTER */}
                <div className="onb-footer">
                    <button className="btn btn-secondary" onClick={handleComplete}>Skip</button>
                    
                    <div className="onb-dots">
                        {Array.from({ length: totalSlides }).map((_, idx) => (
                            <div 
                                key={idx} 
                                className={`dot ${idx === currentSlide ? 'active' : ''} ${idx < currentSlide ? 'passed' : ''}`} 
                            />
                        ))}
                         <span className="onb-dots-text">
                            {currentSlide + 1} / {totalSlides}
                        </span>
                    </div>

                    <div style={{display: 'flex', gap: '10px'}}>
                        <button className="onb-btn secondary" onClick={prevSlide} disabled={currentSlide === 0} style={{opacity: currentSlide === 0 ? 0.5 : 1}}>
                            <span className="material-symbols-outlined">arrow_back</span>
                            <span className="button-text">Back</span>
                        </button>

                        <button className="onb-btn" onClick={nextSlide}>
                             {currentSlide === totalSlides - 1 ? (
                                <>
                                <span className="material-symbols-outlined">check</span>
                                <span className="button-text">Finish</span>
                                </>
                             ) : (
                                <>
                                <span className="material-symbols-outlined">arrow_forward</span>
                                <span className="button-text">Next</span>
                                </>
                             )}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InteractiveOnboarding;